package org.multiplatform.kotfolio.pages.kotlin.new_feature

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.multiplatform.kotfolio.components.AppearanceAwareImage
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Constants
import org.multiplatform.kotfolio.util.Resources

@Page("/kotlin/new_feature/2.0")
@Composable
fun NewFeature_2_0Page() {
    BlogLayout(
        PageProperties(
            "Kotlin 2.0",
            ""
        ),
        BlogProperties("kotlin", "Kotlin 2.0")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    //gradle.properties

                    kotlin.compiler.useK2=true
                    kotlin.compiler.progressive=true
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // After (using data object)
                    data object MyFunctionOptions {
                        var param1: Int = 0
                        var param2: String = ""
                        var param3: Boolean = false
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    enum class Status {
                        Ok, Fail
                    }

                    fun process (
                    	status: Status = Ok
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Smart Casts Evolved
                    fun lenOrNull(x: Any?): Int? {
                        if (x is String || x is StringBuilder) {
                            return (x as CharSequence).length // safe & concise in 2.x contexts
                        }
                        return null
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //enumEntries()
                    enum class Role { ADMIN, EDITOR, VIEWER }

                    val all: List<Role> = enumEntries<Role>().toList()
                    println(all) // [ADMIN, EDITOR, VIEWER]

                    for (r in Role.entries) println(r.name)
                    """.trimIndent()
            )

            //String Literals
            KotlinCode(
                code = """
                    //Enhanced Type Inference
                    val numbers = listOf(1, 2, 3)

                    //New Collection Builders
                    val names = buildList {
                        add("Alice")
                        add("Bob")
                        add("Charlie")
                    }

                    val nameSet = buildSet {
                        add("Alice")
                        add("Bob")
                        add("Charlie")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Multiple contexts
                    //Managing dependencies across layers (e.g., ViewModels, Repositories) can clutter Android projects. Context receivers simplify this by eliminating the need for repeated arguments or dependency injection boilerplate.

                    context(UserPreferences, ThemeManager)
                    @Composable
                    fun Greeting() {
                        Text(text = "Hello, ${getUserName()}", color = themeColor)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    context (viewModel: ViewModel)
                    fun <T> Flow<T>.stateInWhileSubscribed(initialValue: T): StateFlow<T> {
                        return this.stateIn(
                            viewModel.viewModelScope,
                            started = SharingStarted.WhileSubscribed(stopTimeoutMillis = 5000),
                            initialValue
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Value Classes (Inline Classes) Enhancements
                    //Value classes (an evolution of inline classes) let you represent lightweight, type-safe data without the overhead of traditional objects.

                    //1
                    @JvmInline
                    value class Meter(val value: Double) {
                        fun toCentimeters() = value * 100
                        fun toKilometers() = value / 1000
                    }

                    val distance = Meter(5.0)
                    println(distance.toCentimeters()) // Output: Distance in cm: 500.0

                    //Inline Classes Interoperability
                    @JvmInline
                    value class Username(val value: String)

                    fun greetUser(username: Username) {
                        println(username.value)
                    }

                    //2
                    @JvmInline
                    value class UserId(val value: String)

                    data class UserState(
                        val id: UserId,
                        val name: String,
                        val isOnline: Boolean
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "contracts - (Validators, Preconditions (require, check style), Scope, Internal utility libraries, DSL-style builders, Core modules used across teams)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import kotlin.contracts.*

                    @OptIn(ExperimentalContracts::class, ExperimentalExtendedContracts::class)
                    fun decode(encoded: String?): String? {
                        contract {
                            // Guarantees a non-null return value when the input is non-null
                            (encoded != null) implies (returnsNotNull())
                        }
                        if (encoded == null) return null
                        return java.net.URLDecoder.decode(encoded, "UTF-8")
                    }

                    fun useDecodedValue(s: String?) {
                        // Uses a safe call since the return value may be null
                        decode(s)?.length
                        
                        if (s != null) {
                            // Treats the return value as non-null after the smart cast
                            decode(s).length
                        }
                    }

                    fun main() {
                        val decoded = decode("Kotlin multiplatform")
                        println(decoded.length)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Contracts API Enhancements

                    import kotlin.contracts.*

                    inline fun <T> requireNonNull(value: T?): T {
                        contract {
                            returns() implies (value != null)
                        }
                        if (value == null) throw IllegalArgumentException("Value cannot be null")
                        return value
                    }

                    fun main() {
                        val nonNullValue = requireNonNull("Hello")
                        println(nonNullValue.length) // Safe to access length
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Improved Flow API

                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.flow.*

                    fun main() = runBlocking {
                        flowOf(1, 2, 3, 4, 5)
                            .transform { value ->
                                emit(value * value)
                            }
                            .collect { result ->
                                println(result)
                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Type Aliases for Functions

                    typealias StringTransformer = (String) -> String

                    fun applyTransformation(input: String, transformer: StringTransformer): String {
                        return transformer(input)
                    }

                    val toUpperCase: StringTransformer = { it.uppercase() }

                    fun main() {
                        println(applyTransformation("hello", toUpperCase)) // Output: HELLO
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Kotlin code calling a C function
                    import kotlinx.cinterop.*

                    fun main() {
                        memScoped {
                            val buffer = allocArray<ByteVar>(1024)
                            val length = cFunction(buffer, 1024)
                        }
                    }

                    @CName("cFunction")
                    external fun cFunction(buffer: CPointer<ByteVar>, maxLength: Int): Int
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Sealed Interfaces
                    sealed interface ApiResponse

                    data class Success(val data: String) : ApiResponse
                    data class Error(val message: String) : ApiResponse

                    fun handleResponse(response: ApiResponse) {
                        when (response) {
                            is Success -> println(response.data)
                            is Error -> println(response.message)
                        }
                    }

                    fun main() {
                        val success = Success("Data loaded")
                        val error = Error("Failed to load data")

                        handleResponse(success) // Output: Data: Data loaded
                        handleResponse(error)   // Output: Error: Failed to load data
                    }
                    """.trimIndent()
            )

            //Name-based destruction
            KotlinCode(
                code = """
                    //Position base destruction
                     data class Take(val title: String, val speackerName: String)
                     val (title, speackerName) = Take
                     
                     //2.4 Kotlin Version
                     (val title, val speackerName) = taks

                    //Name-based destruction
                    data class User(val id: Int, val name: String)

                    fun doIt(user: User) {
                        val (id, name) = user
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val pair = 3 to "Three"
                    val (number, text) = pair
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val pair = 3 to "Three"

                    println(pair.first)  // Output: 3
                    println(pair.second) // Output: Three

                    fun divide(dividend: Int, divisor: Int): Pair<Int, Int> {
                        val quotient = dividend / divisor
                        val remainder = dividend % divisor
                        return Pair(quotient, remainder)
                    }

                    val result = divide(10, 3)
                    println(result.first)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val items = listOf(
                        "Apple" to "Fruit",
                        "Carrot" to "Vegetable",
                        "Banana" to "Fruit",
                        "Broccoli" to "Vegetable"
                    )

                    val groupedByCategory = items.groupBy { it.second }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun divideExtended(dividend: Int, divisor: Int): Triple<Int, Int, Boolean> {
                        val quotient = dividend / divisor
                        val remainder = dividend % divisor
                        val isExact = remainder == 0
                        return Triple(quotient, remainder, isExact)
                    }

                    val result = divideExtended(10, 3)
                    println(result.third)
                    """.trimIndent()
            )

            //Non local break / continue
            KotlinCode(
                code = """
                    val list = listOf(1, 2, 3, 4, 5)

                    list.forEach{ i ->
                        when{
                            i == 0 -> continue
                            i == 1 -> break
                            else -> println(i)
                        }
                    }
                    """.trimIndent()
            )

            //Explicit Backing Fields
            KotlinCode(
                code = """
                    class LoginViewModel() : ViewModel()
                    {
                        val uiState : StateFlow<LoginStates> field = MutableStateFlow<LoginStates>()
                    }
                    """.trimIndent()
            )

            //Combination of Long and Int types are possible
            KotlinCode(
                code = """
                    fun increment(list: MutableList<Long>){
                        list[0] = list[0] + 1
                    }
                    """.trimIndent()
            )

            //Kotlin 2.x it is possible with the += operator
            KotlinCode(
                code = """
                    fun increment(list: MutableList<Long>){
                        list[0] += 1 // No Error
                    }
                    """.trimIndent()
            )

            //Smart Cast from Variables
            KotlinCode(
                code = """
                    class Dog {
                    fun bark()
                    {
                        println("bark") }
                    }
                    fun petAnimal(animal:Any)
                    {
                        val isDog = animal is Dog
                        if (isDog) { animal.bark() }
                    }
                    """.trimIndent()
            )

            //With Guards
            KotlinCode(
                code = """
                    fun render(status: Status) : String =
                        when(status) {
                            Status.Loading -> "Loading"

                            is Status.OK if status.data.isEmpty() -> "No data"
                            is Status.Ok -> status.data.joinToString()

                            is Status.Error if status.isCritical -> "Critical problem"
                            else -> "Unknown problem"
                        }
                    """.trimIndent()
            )

            //Extensible data arguments
            KotlinCode(
                code = """
                    dataarg class ColumnSettings(
                            val reverseLayout: Boolean = false,
                            val verticalArrangement = Arrangement.Vertical = if(!reverseLayout) Arrangement.Top else Arrangement.Bottom
                        )

                    @Composable
                    fun LazyColumn(dataarg args: ColumnSettings) {

                    }
                    """.trimIndent()
            )

            //Kotlin receiver lambda
            KotlinCode(
                code = """
                    fun String.add(
                    block: String.() → String): String {
                        return this + block()
                    }

                    fun main() {
                        println("Hello".add { " World" })
                    }
                    """.trimIndent()
            )

            //Smart casting
            KotlinCode(
                code = """
                    fun petAnimal(animal: Any) {
                        if (animal is Cat) {
                            animal.purr()
                        }
                    }

                    fun checkAndReturn(obj: Any?): String {
                        return if (obj is String) obj else "Not a String"
                    }
                    """.trimIndent()
            )
        }
    }
}

//http://linkedin.com/pulse/whats-new-kotlin-september-2025-rahul-pahuja-4u2gf/?trackingId=1%2FRymFmUTeKcDcn81xlTTA%3D%3D




























